import { useState } from 'react';

import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import MobileDrawer from '#client/components/MobileDrawer';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useSessionStorage from '#client/hooks/useSessionStorage';
import useSkeleton from '#client/hooks/useSkeleton';
import useUnitAccount from '#client/hooks/useUnitAccount';

import { MenuStyled } from './BankingTitle.styles';
import AccountDetails from './components/AccountDetails';

export default function BankingTitle({ isDashboardNotFunded = false }) {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { isDisable } = useDisableDashboardContext();
  const { isHideInstantDepositOption } = useAstraProfileContext();
  const navigate = useNavigate();

  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { loading: loadingUnitAccount, unitAccount, error: errorUnitAccount } = useUnitAccount();
  const bankingPath = useBankingPath();
  const dashboardUrl = `${bankingPath}/dashboard`;
  const [, setIsRedirectDashboardNotFunded] = useSessionStorage(
    `stop_redirect_dashboard_notfunded.${businessEntityUuid}`,
    false
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [openMobileDrawer, setOpenMobileDrawer] = useState(false);
  const open = Boolean(anchorEl);

  const trackAccountDetailsPressed = useButtonPressedCallback({
    elementName: 'account_details_button',
    elementText: 'Account Details',
    appPageName: 'active_banking_dashboard'
  });
  const handleOpen = (event) => {
    trackAccountDetailsPressed();

    if (isDesktop) {
      setAnchorEl(event.currentTarget);
    } else {
      setOpenMobileDrawer(true);
    }
  };

  const handleClose = () => {
    if (isDesktop) {
      setAnchorEl(null);
    } else {
      setOpenMobileDrawer(false);
    }
  };

  const trackGoToBankDashboard = useLinkPressedCallback({
    elementName: 'go_to_dashboard_notfunded',
    elementText: 'Go to dashboard',
    appPageName: isHideInstantDepositOption
      ? 'banking_account_notfunded_dashboard'
      : 'banking_account_not_funded_dashboard_with_astra',
    destination: dashboardUrl,
    appSection: 'money'
  });

  const handleGoToBankDashboard = () => {
    trackGoToBankDashboard();
    setIsRedirectDashboardNotFunded(true);
    navigate(dashboardUrl);
    handleClose();
  };

  const isHideSkeleton = errorUnitAccount || (unitAccount && !loadingUnitAccount);
  const [showSkeleton] = useSkeleton(isHideSkeleton);

  const accountDetails = showSkeleton ? (
    <Skeleton
      aria-label="loading account details"
      height="30px"
      width="100px"
    />
  ) : (
    <Button
      aria-controls={open ? 'account-details-menu' : undefined}
      aria-expanded={open}
      aria-haspopup
      color="primary"
      disabled={isDisable}
      disableRipple
      endIcon={<KeyboardArrowDownIcon />}
      id="account-details-button"
      onClick={handleOpen}
      size="md"
      variant="text"
    >
      Account Details
    </Button>
  );

  const goToDashboard = (
    <Button
      color="primary"
      onClick={handleGoToBankDashboard}
      size="small"
      variant="text"
    >
      Go to dashboard
    </Button>
  );

  return (
    <>
      <Box
        alignItems={{ xs: 'left', md: 'center' }}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap="8px"
        justifyContent="space-between"
        marginBottom={{ xs: '16px', md: '48px' }}
        width="100%"
      >
        <Heading
          family="secondary"
          size="3xl"
          weight="bold"
        >
          Business Banking
        </Heading>
        <Stack
          alignItems="center"
          direction="row"
          gap={{ xs: '16px', md: '32px' }}
        >
          {isDashboardNotFunded ? goToDashboard : accountDetails}
        </Stack>
      </Box>
      {isDesktop && (
        <MenuStyled
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          disableAutoFocus
          id="account-details-menu"
          MenuListProps={{
            'aria-labelledby': 'account-details-button'
          }}
          onClose={handleClose}
          open={open}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Stack
            minHeight={40}
            minWidth={245}
            spacing="8px"
          >
            <AccountDetails />
          </Stack>
        </MenuStyled>
      )}
      {!isDesktop && openMobileDrawer && (
        <MobileDrawer
          aria-labelledby="account-details-button"
          handleClose={() => handleClose('')}
          id="account-details-menu"
        >
          <Stack spacing="8px">
            <AccountDetails />
          </Stack>
        </MobileDrawer>
      )}
    </>
  );
}

BankingTitle.propTypes = {
  isDashboardNotFunded: PropTypes.bool
};
